import React from 'react';
import { useTranslation } from 'react-i18next';

import { BackHeader } from '../../../../components';
import { areaTitle } from '../../area-copy';
import { useBackToWebsite } from '../../hub-navigation';
import type { HeaderOverride } from '../../header-action';
import { HeaderTag } from './WebsiteAreaHeader.styles';
import type { WebsiteTab } from '../../tabs';

export type WebsiteAreaHeaderProps = {
  /** The area on screen: its name heads the page. */
  tab: WebsiteTab;
  /** The open area's action, at the row's right (on a phone, on a row of its own under the name). */
  action?: React.ReactNode;
  /** The open area's page names the header and takes its back button (see `useWebsiteHeaderOverride`). */
  override?: HeaderOverride | null;
};

/**
 * A Website area page's header: the app's drilldown header (`BackHeader`, as on every drill-in page), its back
 * button going to the hub (it pops back when the area was opened from the hub, so the page slides back, and pushes
 * the hub otherwise), the area's name as its centred title and, at the right, the open area's action when it has one
 * (see `useWebsiteHeaderAction`). Moving between areas goes through the hub. Rendered
 * unwrapped: BackHeader is sticky, and it pins under the top bar only while its parent is the page column.
 */
export const WebsiteAreaHeader = ({ tab, action, override = null }: WebsiteAreaHeaderProps): React.ReactElement => {
  const { t } = useTranslation();
  const backToWebsite = useBackToWebsite();
  return (
    <BackHeader
      title={override?.title ?? areaTitle(t, tab.id)}
      titleRaw={override?.title !== undefined}
      backLabel={override?.backLabel ?? t('Back to Website')}
      onBack={override?.onBack ?? backToWebsite}
      onTitleClick={override?.onTitleClick}
      titleIcon={override?.titleIcon}
      titleLabel={override?.titleLabel}
      subtitle={override?.tag ? <HeaderTag text={override.tag} tag="span" raw /> : undefined}
      action={action}
      stackActionOnMobile={Boolean(action) && !override?.inlineAction}
      narrowAction={Boolean(override?.inlineAction)}
      actionOpen={Boolean(override?.actionOpen)}
    />
  );
};

export default WebsiteAreaHeader;
